import { Box } from "@mui/material";
import { SuspenseFallback } from "common/components/LoadingScreen";
import { UpdateBiomassAfterMoveInDialog } from "features/singleCoverInput";
import { MutableRefObject, ReactNode, Suspense } from "react";
import { MapRef } from "react-map-gl/mapbox";
import { useLocation } from "react-router-dom";

interface MobileLayoutProps {
  children: ReactNode;
  mapRef: MutableRefObject<MapRef | null>;
}

/**
 * Mobile layout component that renders content full-screen.
 * Map is rendered at App level, this just handles content positioning.
 */
export default function MobileLayout({ children, mapRef }: MobileLayoutProps) {
  const location = useLocation();

  // Routes where the map should be interactive and the component handles its own layout
  // These routes render children directly without a full-viewport wrapper
  // Includes all bottom sheet routes that should show the map behind them
  const isMapInteractiveRoute =
    location.pathname.includes("/edit-field") ||
    location.pathname.includes("/add-field") ||
    location.pathname.includes("/add-farm") ||
    location.pathname.match(/^\/farm\/[^/]+$/) || // /farm/:farmId (exact match)
    location.pathname.match(/^\/field\/[^/]+$/) || // /field/:fieldId
    location.pathname.includes("/fields-list/") ||
    location.pathname.includes("/animal-group/") ||
    location.pathname.includes("/add-animal-group");

  // For map interactive routes, render children directly - they handle their own positioning
  // This allows the map underneath to receive touch events for pan/zoom
  if (isMapInteractiveRoute) {
    return (
      <>
        <UpdateBiomassAfterMoveInDialog />
        <Suspense fallback={<SuspenseFallback />}>{children}</Suspense>
      </>
    );
  }

  // For non-map routes (showMap: false), render full-screen content over the map
  return (
    <>
      {/* Main content layer */}
      <Box
        sx={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          width: "100%",
          height: "100vh",
          overflow: "auto",
          paddingBottom: "80px", // Space for bottom navigation
          backgroundColor: "white",
          // z-index must be higher than the map (which has zIndex: drawer - 1 = 1199)
          zIndex: 1300,
        }}
      >
        <UpdateBiomassAfterMoveInDialog />
        <Suspense fallback={<SuspenseFallback />}>{children}</Suspense>
      </Box>
    </>
  );
}
